// src/components/resumo/annotations/UnifiedAnnotationToolbar.tsx

import React, { useState } from 'react';
import { HighlightColor, NoteCategory } from './annotations.types';
import {
    Highlighter, Save, X, Minus, AlignCenter, Maximize2, Trash2,
    Star, BookOpen, HelpCircle, Lightbulb, StickyNote, MessageSquare,
    Eye, EyeOff, Undo2, Redo2, ChevronDown
} from 'lucide-react';

interface UnifiedAnnotationToolbarProps {
    isHighlighting: boolean;
    onToggleHighlighting: () => void;
    isRemoving: boolean;
    onToggleRemoving: () => void;
    selectedColor: HighlightColor;
    onColorChange: (color: HighlightColor) => void;
    selectedThickness: 'thin' | 'medium' | 'thick';
    onThicknessChange: (thickness: 'thin' | 'medium' | 'thick') => void;
    selectedStyle: 'solid' | 'underline' | 'translucent';
    onStyleChange: (style: 'solid' | 'underline' | 'translucent') => void;
    activeNoteCategory: NoteCategory | null;
    onToggleNoteCategory: (category: NoteCategory) => void;
    onCreateFreeNote: () => void;
    isCommenting: boolean;
    onToggleCommenting: () => void;
    commentCount?: number;
    showComments?: boolean;
    onToggleShowComments?: () => void;
    onSave: () => void;
    onClose: () => void;
    // Undo/Redo
    onUndo?: () => void;
    onRedo?: () => void;
    canUndo?: boolean;
    canRedo?: boolean;
    darkMode?: boolean;
}

// ====== PALETA DE CORES ======
// 9 principais (sempre visíveis) — inclui Verde GRAIA e Azul Prussiano
const MAIN_COLORS: { value: HighlightColor; hex: string; label: string }[] = [
    { value: 'yellow',  hex: '#fbbf24', label: 'Amarelo' },
    { value: 'green',   hex: '#10b981', label: 'Verde' },
    { value: 'blue',    hex: '#3b82f6', label: 'Azul' },
    { value: 'pink',    hex: '#ec4899', label: 'Rosa' },
    { value: 'orange',  hex: '#f97316', label: 'Laranja' },
    { value: 'graia',   hex: '#0D3B0E', label: 'Verde GRAIA' },
    { value: 'prussian',hex: '#2a4a5a', label: 'Azul Prussiano' },
    { value: 'red',     hex: '#ef4444', label: 'Vermelho' },
    { value: 'purple',  hex: '#8b5cf6', label: 'Roxo' },
];

// Cores extras (paleta expandida)
const EXTRA_COLORS: { value: HighlightColor; hex: string; label: string }[] = [
    { value: 'teal',        hex: '#14b8a6', label: 'Teal' },
    { value: 'cyan',        hex: '#06b6d4', label: 'Ciano' },
    { value: 'lime',        hex: '#84cc16', label: 'Lima' },
    { value: 'amber',       hex: '#f59e0b', label: 'Âmbar' },
    { value: 'rose',        hex: '#f43f5e', label: 'Rosa escuro' },
    { value: 'violet',      hex: '#7c3aed', label: 'Violeta' },
    { value: 'indigo',      hex: '#4338ca', label: 'Índigo' },
    { value: 'gray',        hex: '#6b7280', label: 'Cinza' },
    { value: 'dark',        hex: '#1f2937', label: 'Escuro' },
    { value: 'white',       hex: '#ffffff', label: 'Branco' },
    { value: 'sky',         hex: '#0ea5e9', label: 'Céu' },
    { value: 'emerald',     hex: '#059669', label: 'Esmeralda' },
    // Pastéis
    { value: 'lemon',       hex: '#FFFDE7', label: 'Limão pastel' },
    { value: 'sage',        hex: '#E8F5E9', label: 'Sage pastel' },
    { value: 'powder',      hex: '#E1F5FE', label: 'Azul pó' },
    { value: 'blush',       hex: '#FCE4EC', label: 'Blush pastel' },
    { value: 'peach',       hex: '#FFF3E0', label: 'Pêssego pastel' },
    { value: 'lavender',    hex: '#EDE7F6', label: 'Lavanda pastel' },
    { value: 'aquamarine',  hex: '#E0F2F1', label: 'Água-marinha pastel' },
    { value: 'salmon',      hex: '#FFEBEE', label: 'Salmão pastel' },
    { value: 'periwinkle',  hex: '#E8EAF6', label: 'Periwinkle pastel' },
    { value: 'honeydew',    hex: '#F1F8E9', label: 'Honeydew pastel' },
    { value: 'champagne',   hex: '#FFF8E1', label: 'Champanhe pastel' },
    { value: 'lilac',       hex: '#F3E5F5', label: 'Lilás pastel' },
];

const ALL_COLORS = [...MAIN_COLORS, ...EXTRA_COLORS];

const UnifiedAnnotationToolbar: React.FC<UnifiedAnnotationToolbarProps> = ({
    isHighlighting,
    onToggleHighlighting,
    isRemoving,
    onToggleRemoving,
    selectedColor,
    onColorChange,
    selectedThickness,
    onThicknessChange,
    selectedStyle,
    onStyleChange,
    activeNoteCategory,
    onCreateFreeNote,
    onToggleNoteCategory,
    isCommenting,
    onToggleCommenting,
    commentCount = 0,
    showComments = true,
    onToggleShowComments,
    onSave,
    onClose,
    onUndo,
    onRedo,
    canUndo = false,
    canRedo = false,
    darkMode = false
}) => {
    const [showMoreColors, setShowMoreColors] = useState(false);

    const selectedHex = ALL_COLORS.find(c => c.value === selectedColor)?.hex || '#fbbf24';

    const thicknesses = [
        { value: 'thin' as const, icon: Minus, label: 'Fino' },
        { value: 'medium' as const, icon: AlignCenter, label: 'Médio' },
        { value: 'thick' as const, icon: Maximize2, label: 'Grosso' }
    ];

    const styles = [
        { value: 'solid' as const, label: 'Sólido', char: '█' },
        { value: 'underline' as const, label: 'Sublinhado', char: 'U̲' },
        { value: 'translucent' as const, label: 'Translúcido', char: '▒' }
    ];

    const noteCategories: { value: NoteCategory; icon: any; label: string; color: string }[] = [
        { value: 'importante', icon: Star, label: 'Importante', color: '#fbbf24' },
        { value: 'revisar', icon: BookOpen, label: 'Revisar', color: '#3b82f6' },
        { value: 'duvida', icon: HelpCircle, label: 'Dúvida', color: '#ef4444' },
        { value: 'conceito', icon: Lightbulb, label: 'Conceito', color: '#10b981' }
    ];

    return (
        <div className={`unified-annotation-toolbar ${darkMode ? 'dark' : ''}`}>
            <div className="uat-container">

                {/* ====== UNDO / REDO ====== */}
                <div className="uat-section uat-section-history">
                    <div className="uat-section-header">
                        <span className="uat-section-title">Histórico</span>
                    </div>
                    <div className="uat-section-content">
                        <button
                            className="uat-icon-btn"
                            onClick={onUndo}
                            disabled={!canUndo}
                            title="Desfazer (Ctrl+Z)"
                        >
                            <Undo2 size={15} />
                        </button>
                        <button
                            className="uat-icon-btn"
                            onClick={onRedo}
                            disabled={!canRedo}
                            title="Refazer (Ctrl+Y)"
                        >
                            <Redo2 size={15} />
                        </button>
                    </div>
                </div>

                <div className="uat-main-divider" />

                {/* ====== GRIFO ====== */}
                <div className="uat-section uat-section-highlight">
                    <div className="uat-section-header">
                        <span className="uat-section-title">Grifar</span>
                    </div>

                    <div className="uat-section-content">
                        <button
                            className={`uat-highlight-toggle ${isHighlighting ? 'active' : ''}`}
                            onClick={onToggleHighlighting}
                            title={isHighlighting ? 'Desativar modo grifo' : 'Ativar modo grifo'}
                            data-tutorial-target="highlight-activate"
                        >
                            <Highlighter size={16} />
                            <span>{isHighlighting ? 'Ativo' : 'Ativar'}</span>
                        </button>

                        <button
                            className={`uat-icon-btn uat-remove-toggle ${isRemoving ? 'active' : ''}`}
                            onClick={onToggleRemoving}
                            title={isRemoving ? 'Desativar modo remover' : 'Remover grifos'}
                        >
                            <Trash2 size={16} />
                        </button>

                        <div className="uat-divider" />

                        {/* Grid compacto de 9 cores + botão "mais" */}
                        <div className="uat-colors-compact">
                            <div className="uat-colors-grid">
                                {MAIN_COLORS.map(color => (
                                    <button
                                        key={color.value}
                                        className={`uat-color-sq ${selectedColor === color.value ? 'selected' : ''}`}
                                        style={{ background: color.hex }}
                                        onClick={() => onColorChange(color.value)}
                                        title={color.label}
                                        disabled={!isHighlighting}
                                    />
                                ))}
                                <div className="uat-more-colors-wrapper">
                                    <button
                                        className="uat-color-sq uat-more-btn"
                                        onClick={() => setShowMoreColors(v => !v)}
                                        title="Mais cores"
                                        disabled={!isHighlighting}
                                        style={{ background: showMoreColors ? '#e5e7eb' : undefined }}
                                    >
                                        <ChevronDown size={12} />
                                    </button>
                                    {showMoreColors && (
                                        <div className="uat-extra-palette">
                                            <div className="uat-extra-grid">
                                                {/* Mostrar todas as cores (main + extra) na paleta expandida */}
                                                {ALL_COLORS.map(color => (
                                                    <button
                                                        key={color.value}
                                                        className={`uat-color-sq ${selectedColor === color.value ? 'selected' : ''}`}
                                                        style={{ background: color.hex, border: color.hex === '#ffffff' ? '1px solid #d1d5db' : undefined }}
                                                        onClick={() => {
                                                            onColorChange(color.value);
                                                            setShowMoreColors(false);
                                                        }}
                                                        title={color.label}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Indicador da cor ativa */}
                            <div
                                className="uat-active-color-bar"
                                style={{ background: selectedHex }}
                                title={`Cor ativa: ${ALL_COLORS.find(c => c.value === selectedColor)?.label || selectedColor}`}
                            />
                        </div>

                        <div className="uat-divider" />

                        {/* Espessura + Estilo em grid 3×2 */}
                        <div className="uat-thick-style-grid">
                            {thicknesses.map(({ value, icon: Icon, label }) => (
                                <button
                                    key={value}
                                    className={`uat-icon-btn ${selectedThickness === value ? 'selected' : ''}`}
                                    onClick={() => onThicknessChange(value)}
                                    title={label}
                                    disabled={!isHighlighting}
                                >
                                    <Icon size={13} />
                                </button>
                            ))}
                            {styles.map(({ value, label, char }) => (
                                <button
                                    key={value}
                                    className={`uat-icon-btn ${selectedStyle === value ? 'selected' : ''}`}
                                    onClick={() => onStyleChange(value)}
                                    title={label}
                                    disabled={!isHighlighting}
                                >
                                    {char}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="uat-main-divider" />

                {/* ====== NOTAS ====== */}
                <div className="uat-section uat-section-notes">
                    <div className="uat-section-header">
                        <span className="uat-section-title">Notas</span>
                    </div>
                    <div className="uat-section-content">
                        <button
                            className="uat-note-btn"
                            onClick={onCreateFreeNote}
                            title="Criar nova nota"
                        >
                            <StickyNote size={16} />
                            <span>Criar nota</span>
                        </button>

                        <div className="uat-divider" />

                        <div className="uat-note-categories-group" data-tutorial-target="notes-categories">
                            {noteCategories.map(({ value, icon: Icon, label, color }) => (
                                <button
                                    key={value}
                                    className={`uat-icon-btn uat-note-category-btn ${activeNoteCategory === value ? 'active' : ''}`}
                                    onClick={() => onToggleNoteCategory(value)}
                                    title={label}
                                    style={{
                                        borderColor: activeNoteCategory === value ? color : 'transparent',
                                        color: activeNoteCategory === value ? color : undefined
                                    }}
                                >
                                    <Icon size={16} />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="uat-main-divider" />

                {/* ====== COMENTÁRIOS ====== */}
                <div className="uat-section uat-section-comments">
                    <div className="uat-section-header">
                        <span className="uat-section-title">Comentários</span>
                    </div>
                    <div className="uat-section-content">
                        <button
                            className={`uat-highlight-toggle ${isCommenting ? 'active' : ''}`}
                            onClick={onToggleCommenting}
                            title={isCommenting ? 'Desativar modo comentário' : 'Ativar modo comentário e selecionar um texto'}
                            style={isCommenting ? { borderColor: 'var(--platform-accent)' } : {}}
                            data-tutorial-target="comments-toggle"
                        >
                            <MessageSquare size={16} />
                            <span>{isCommenting ? 'Ativo' : 'Comentar'}</span>
                            {commentCount > 0 && (
                                <span style={{
                                    background: 'var(--platform-accent)', color: 'white',
                                    borderRadius: '10px', padding: '1px 6px',
                                    fontSize: '10px', fontWeight: 700, marginLeft: '2px',
                                }}>
                                    {commentCount}
                                </span>
                            )}
                        </button>

                        {commentCount > 0 && onToggleShowComments && (
                            <button
                                className="uat-icon-btn"
                                onClick={onToggleShowComments}
                                title={showComments ? 'Esconder comentários' : 'Mostrar comentários'}
                                style={{ color: showComments ? undefined : '#9ca3af' }}
                            >
                                {showComments ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                        )}
                    </div>
                </div>

                {/* ====== AÇÕES ====== */}
                <div className="uat-actions">
                    <button className="uat-action-btn uat-save" onClick={onSave} title="Salvar Anotações">
                        <Save size={14} />
                        <span>Salvar</span>
                    </button>
                    <button className="uat-action-btn uat-close" onClick={onClose} title="Sair do Modo Interagir">
                        <X size={14} />
                        <span>Fechar</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnifiedAnnotationToolbar;
